import { fetchIntegrationRequest } from '@api';
import type { Attachment, IntegrationRequestBundle } from '@domain/types';
import {
  fetchDocumentAttachment,
  fetchIntegrationRequestV2Detail,
  fetchPossibleDuplicateAttachment,
  fetchPossibleDuplicateV2,
} from '@lifen-integration/features/integrationRequests/services';
import { QueryClient, useQueries, useQuery } from '@tanstack/react-query';

const INTEGRATION_REQUESTS_STALE_TIME = 5 * 60 * 1000; // 5min
const ATTACHMENT_STALE_TIME = 4 * 24 * 60 * 60 * 1000; // 4 days

export const integrationRequestQueryKey = (id: string) => ['integrationRequest', id];

export function useIntegrationRequest(id: string) {
  return useQuery({
    queryKey: integrationRequestQueryKey(id),
    queryFn: () => fetchIntegrationRequest(id),
    staleTime: INTEGRATION_REQUESTS_STALE_TIME,
  });
}

/**
 * MIGRATION_V2 — retiring the v1 bundle on the detail screen.
 *
 * The detail components read `useIntegrationRequestV2Detail`; `useIntegrationRequest` still serves
 * the v1 bundle to what is left. In order:
 *
 *   [x] backend: `integration` (`serializable_integration` and its connectors), `support_reference`
 *       and `temporaryError` on the `:extended` view. This front must not ship ahead of that PR, or
 *       `PatientCard` and the connector rows go silent.
 *   [ ] backend: the full `FhirEncounterBlueprint` on `encounter` — `:light` drops `referent` and
 *       `functionalUnitCode`, which `EncounterBanner` renders, and that is what still ties
 *       `IntegrationCard` to the v1 entity. Tried and reverted back-side; ask before relying on it.
 *   [ ] backend: `Api::V2::ConnectorBlueprint` serves `integration.connectors` raw, where v1
 *       filtered it to the current user's identity devices and appended a disabled row per device
 *       without a `Connector`. `IntegrationCard` therefore renders a different set of rows.
 *   [ ] `Content` and `Details` still take an `IntegrationRequest` prop, only to hand it to
 *       children: `ErrorBanners`, `DocumentActions`. (`DuplicateViewer` now reads the v2 detail.)
 *   [ ] `features/encounters/hooks` and `IntegrationCard`'s encounter block are the last readers of
 *       `document.integration`.
 *   [ ] delete `useIntegrationRequest`, the bundle cache and the cross-invalidation below.
 */
export const integrationRequestV2QueryKey = (id?: string) => ['integrationRequestV2', id];

/**
 * The detail document from `GET /api/v2/integration_requests/:id`.
 *
 * MIGRATION_V2(bridge): runs alongside `useIntegrationRequest`, which keeps serving the v1 bundle to
 * the parts of the tree that have not migrated: two fetches per detail open, on purpose. The
 * readiness rule lives on the server, so it cannot be read off the bundle at any price.
 * Remove when: nothing left. This hook is the one that stays; `useIntegrationRequest` is what goes.
 */
export function useIntegrationRequestV2Detail(id?: string, enabled = true) {
  return useQuery({
    queryKey: integrationRequestV2QueryKey(id),
    queryFn: () => fetchIntegrationRequestV2Detail(id!),
    staleTime: INTEGRATION_REQUESTS_STALE_TIME,
    enabled: enabled && Boolean(id),
  });
}

export const possibleDuplicateV2QueryKey = (documentId: string, id: string) => [
  'possibleDuplicateV2',
  documentId,
  id,
];

export function usePossibleDuplicateV2(documentId: string, id: string, enabled = true) {
  return useQuery({
    queryKey: possibleDuplicateV2QueryKey(documentId, id),
    queryFn: () => fetchPossibleDuplicateV2(documentId, id),
    staleTime: INTEGRATION_REQUESTS_STALE_TIME,
    enabled: enabled && Boolean(documentId && id),
  });
}

/**
 * Keyed by the duplicate's pair of ids, not by the attachment id alone: the route authorises on the
 * source document, so the same attachment is not necessarily readable from another one.
 */
export function usePossibleDuplicateAttachment(
  documentId: string,
  id: string,
  attachmentId?: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['possibleDuplicateAttachment', documentId, id, attachmentId],
    queryFn: () => fetchPossibleDuplicateAttachment(documentId, id),
    // Immutable content, same reasoning as `useDocumentAttachment`.
    staleTime: ATTACHMENT_STALE_TIME,
    gcTime: ATTACHMENT_STALE_TIME,
    enabled: enabled && Boolean(documentId && id && attachmentId),
  });
}

export function useMultipleIntegrationRequests(ids: string[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: integrationRequestQueryKey(id),
      queryFn: () => fetchIntegrationRequest(id),
      staleTime: INTEGRATION_REQUESTS_STALE_TIME,
    })),
  });
}

export const integrationRequestAttachmentQueryKey = (id?: number) => [
  'integrationRequestAttachment',
  id,
];

export function useDocumentAttachment(attachmentId?: number, enabled = true) {
  return useQuery({
    queryKey: integrationRequestAttachmentQueryKey(attachmentId),
    queryFn: () => fetchDocumentAttachment(attachmentId!),
    // The attachment content is immutable: it only changes when a mutation
    // replaces the file (split, merge, import original), and those invalidate
    // this key explicitly. gcTime must match staleTime, otherwise the global
    // 1h gcTime evicts the entry long before it goes stale.
    staleTime: ATTACHMENT_STALE_TIME,
    gcTime: ATTACHMENT_STALE_TIME,
    enabled,
  });
}

/** The integration request's attachment, from `GET /api/v2/document_attachments/:id`. */
export function useIntegrationRequestAttachment(
  documentAttachmentId?: number,
): Attachment | undefined {
  const { data: attachment } = useDocumentAttachment(
    documentAttachmentId,
    Boolean(documentAttachmentId),
  );

  return attachment;
}

/**
 * Attachment of each given document, in the same order.
 *
 * Each document supplies its own `documentAttachmentId`, so the queries start as
 * soon as the ids are known rather than waiting on the bundles' attachments.
 */
export function useMultipleIntegrationRequestAttachments(
  documentAttachmentIds: (number | undefined)[],
): {
  attachments: (Attachment | undefined)[];
  isLoading: boolean;
} {
  const results = useQueries({
    queries: documentAttachmentIds.map((documentAttachmentId) => ({
      queryKey: integrationRequestAttachmentQueryKey(documentAttachmentId),
      queryFn: () => fetchDocumentAttachment(documentAttachmentId!),
      staleTime: ATTACHMENT_STALE_TIME,
      gcTime: ATTACHMENT_STALE_TIME,
      enabled: Boolean(documentAttachmentId),
    })),
  });

  return {
    attachments: results.map((result) => result.data),
    isLoading: results.some((result) => result.isLoading),
  };
}

/**
 * Id of the attachment of a given document, read off its cached bundle.
 *
 * The attachment cache is keyed by attachment id, but mutations only know the
 * document they acted on, so they resolve the attachment id through here.
 * Returns undefined when the bundle is no longer cached, in which case there is
 * no attachment entry to touch either.
 */
function getCachedAttachmentId(queryClient: QueryClient, documentId: string): number | undefined {
  const bundle = queryClient.getQueryData<IntegrationRequestBundle>(
    integrationRequestQueryKey(documentId),
  );

  return bundle?.document?.documentAttachmentId;
}

export async function invalidateIntegrationRequestAttachmentCache(
  queryClient: QueryClient,
  documentId: string,
) {
  const attachmentId = getCachedAttachmentId(queryClient, documentId);

  if (!attachmentId) {
    return;
  }

  await Promise.all([
    queryClient.removeQueries({
      queryKey: integrationRequestAttachmentQueryKey(attachmentId),
      type: 'inactive',
    }),
    queryClient.invalidateQueries({
      queryKey: integrationRequestAttachmentQueryKey(attachmentId),
      type: 'active',
    }),
  ]);
}

/**
 * MIGRATION_V2(bridge): the v2 copy of a document cannot be patched in place the way the bundle is.
 * `isReadyToImport` is recomputed server-side out of the verification pipeline, so a local edit to
 * the bundle can flip it in ways the client cannot predict — dropping the entry is the only correct
 * answer. Costs one refetch per mutation while both caches coexist.
 * Remove when: the bundle is gone and mutations write the v2 entry directly.
 */
function invalidateIntegrationRequestV2Cache(queryClient: QueryClient, id: string) {
  return queryClient.invalidateQueries({ queryKey: integrationRequestV2QueryKey(id) });
}

/**
 * Applies `updater` to the cached document, if there is one, and drops its v2 copy.
 *
 * The guard is not defensive tidying: react-query calls the updater with `undefined` when the
 * entry has been evicted, and every caller spreads its argument. Without it, an update racing a
 * garbage collection writes a partial object back under the key.
 */
export function updateIntegrationRequestCache(
  queryClient: QueryClient,
  id: string,
  updater: (bundle: IntegrationRequestBundle) => IntegrationRequestBundle,
) {
  queryClient.setQueryData<IntegrationRequestBundle>(integrationRequestQueryKey(id), (bundle) =>
    bundle ? updater(bundle) : bundle,
  );

  invalidateIntegrationRequestV2Cache(queryClient, id);
}

export async function invalidateIntegrationRequestCache(queryClient: QueryClient, id: string) {
  await Promise.all([
    queryClient.removeQueries({ queryKey: integrationRequestQueryKey(id), type: 'inactive' }),
    queryClient.invalidateQueries({ queryKey: integrationRequestQueryKey(id), type: 'active' }),
    invalidateIntegrationRequestV2Cache(queryClient, id),
  ]);
}
